import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, Pencil, ShieldCheck } from 'lucide-react';
import { Member, membersApi, membersError } from '../../services/membersApi.ts';
import { membersOnboardingApi, normalizeDocument, normalizeIncome, OnboardingDraft, OnboardingState } from '../../services/membersOnboardingApi.ts';
import {MARKETING_CONSENT_VERSION} from '../../services/membersEmailApi.ts';
import MembersMaskedField from './MembersMaskedField.tsx';
import MembersBankField from './MembersBankField.tsx';
import {digits, nationalPhone, formatPhone, formatPostalCode, formatIncome, incomeFromDigits, states} from './membersInputFormats.ts';
import {lookupPostalCode} from './membersPostalLookup.ts';
import MembersMarketingOptIn from './MembersMarketingOptIn.tsx';
import MembersAsaasSeal from './MembersAsaasSeal.tsx';
import { Card, Field, Loading, MembersShell, Notice, primary } from './MembersShared.tsx';
const labels = ['Seu acesso', 'Titular', 'Endereço', 'Conta bancária', 'Revisão'];
const holderKeys = ['person_type', 'legal_name', 'cpf_cnpj', 'birth_date', 'company_type', 'income_value'] as const;
const addressKeys = ['address', 'address_number', 'complement', 'province', 'postal_code', 'city', 'state'] as const;
const bankKeys = ['bank_code', 'bank_agency', 'bank_agency_digit', 'bank_account', 'bank_account_digit', 'bank_account_type', 'bank_holder_name', 'bank_holder_cpf_cnpj'] as const;
function selectedFields(draft: OnboardingDraft, keys: ReadonlyArray<keyof OnboardingDraft>): OnboardingDraft {
  return Object.fromEntries(keys.map(key => [key, draft[key] ?? '']));
}
function Select({
  label,
  name,
  value,
  onChange,
  children
}: React.PropsWithChildren<{
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
}>) {
  return <label className="block space-y-2 text-sm">
    <span>{label} *</span>
    <select className="members-input w-full" name={name} value={value} required onChange={event => onChange(event.target.value)}>{children}</select>
  </label>;
}
function AsaasExplanation() {
  return <aside className="lg:sticky lg:top-8">
    <Card>
      <ShieldCheck className="members-link mb-4" size={28} />
      <h2 className="text-lg font-medium">Seu recebimento com o Asaas</h2>
      <p className="members-secondary mt-3 text-sm leading-relaxed">Os dados do titular e do endereço são usados para abrir sua conta de recebimento no Asaas. Você poderá acompanhar as próximas etapas pelo Members.</p>
      <p className="members-secondary mt-3 text-sm leading-relaxed">A conta bancária deve pertencer ao mesmo titular. Confira os dados antes do envio. A abertura da conta de recebimento depende da análise cadastral do Asaas.</p>
      <MembersAsaasSeal />
    </Card>
  </aside>;
}
export default function MembersOnboarding() {
  const [member, setMember] = useState<Member | null>(null);
  const [snapshot, setSnapshot] = useState<OnboardingState | null>(null);
  const [draft, setDraft] = useState<OnboardingDraft>({
    person_type: 'PF',
    bank_account_type: 'CHECKING'
  });
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  const [registrationUnavailable, setRegistrationUnavailable] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [consent, setConsent] = useState(false);
  const [retry, setRetry] = useState(0);
  const [phone, setPhone] = useState('');
  const [postalRequest, setPostalRequest] = useState<{cep: string; baseline: OnboardingDraft} | null>(null);
  const [postalStatus, setPostalStatus] = useState('');
  useEffect(() => {
    if (!postalRequest) return;
    if (step !== 3 || snapshot?.locked || uncertain) { setPostalRequest(null); setPostalStatus(''); return; }
    let current = true;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 8000);
    setPostalStatus('Buscando endereço…');
    lookupPostalCode(postalRequest.cep, controller.signal).then(address => {
      if (!current) return;
      if (!address) {
        setPostalStatus('CEP não encontrado. Confira o CEP ou preencha o endereço manualmente.');
        return;
      }
      setDraft(previous => {
        if (!current || digits(previous.postal_code || '') !== postalRequest.cep) return previous;
        const updated = {...previous};
        for (const key of ['address', 'province', 'city', 'state'] as const) {
          if (previous[key] === postalRequest.baseline[key] && address[key]) updated[key] = address[key];
        }
        return updated;
      });
      setPostalStatus('Consulta concluída. Confira e complete o endereço.');
    }).catch(() => {
      if (current) setPostalStatus('Não foi possível buscar o CEP. Preencha o endereço manualmente.');
    }).finally(() => window.clearTimeout(timer));
    return () => { current = false; controller.abort(); window.clearTimeout(timer); };
  }, [postalRequest, step, snapshot?.locked, uncertain]);
  useEffect(() => {
    let current = true;
    setLoading(true);
    setError('');
    setLoadFailed(false);
    setRegistrationUnavailable(false);
    async function restore() {
      let authenticated = false;
      try {
        const account = await membersApi.me();
        authenticated = true;
        if (current) { setMember(account); setPhone(formatPhone(account.phone || '')); }
        const state = await membersOnboardingApi.get();
        if (current) {
          setMember(account);
          setSnapshot(state);
          setDraft({
            person_type: 'PF',
            bank_account_type: 'CHECKING',
            ...state.draft
          });
          setStep(state.locked ? 5 : Math.min(5, Math.max(2, state.step)));
          setConsent(state.consent);
          setUncertain(false);
        }
      } catch (err) {
        if (!current) return;
        if ((err as {
          response?: {
            status?: number;
          };
        })?.response?.status === 401 && !authenticated) {
          setMember(null);
          setStep(1);
        } else if ((err as {response?: {status?: number}})?.response?.status === 404 && !authenticated) {
          setMember(null);
          setStep(1);
          setRegistrationUnavailable(true);
        } else {
          setError(membersError(err));
          setLoadFailed(true);
        }
      } finally {
        if (current) setLoading(false);
      }
    }
    void restore();
    return () => {
      current = false;
    };
  }, [retry]);
  const locked = snapshot?.locked || uncertain;
  function change(key: keyof OnboardingDraft, value: string) {
    setDraft(current => ({
      ...current,
      [key]: value
    }));
    setConsent(false);
  }
  function field(label: string, key: keyof OnboardingDraft, options: React.InputHTMLAttributes<HTMLInputElement> = {}) {
    return <Field label={label} name={key} value={String(draft[key] ?? '')} onChange={event => change(key, event.target.value)} required {...options} />;
  }
  async function register(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (registrationUnavailable) return;
    if (member) {
      setStep(2);
      return;
    }
    const values = new FormData(event.currentTarget);
    setBusy(true);
    setError('');
    let created = false;
    try {
      const account = await membersApi.register({
        name: String(values.get('name')).trim(),
        email: String(values.get('email')).trim(),
        phone: nationalPhone(String(values.get('phone'))),
        password: String(values.get('password')),
        marketing_opt_in: values.get('marketing_opt_in') === 'on',
        ...(values.get('marketing_opt_in') === 'on' ? {marketing_consent_version: MARKETING_CONSENT_VERSION} : {})
      });
      // Senha nunca sai do formulário/escopo desta requisição para storage.
      setMember(account);
      created = true;
      const state = await membersOnboardingApi.get();
      setSnapshot(state);
      setDraft({
        person_type: 'PF',
        bank_account_type: 'CHECKING',
        ...state.draft
      });
      setStep(2);
    } catch (err) {
      if (created) {
        setError('Seu acesso foi criado, mas não conseguimos carregar a próxima etapa. Tente novamente para retomar.');
        setLoadFailed(true);
      } else if (!(err as {
        response?: unknown;
      })?.response) {
        // Uma resposta perdida não autoriza repetir a criação do acesso.
        try {
          const account = await membersApi.me();
          setMember(account);
          created = true;
          const state = await membersOnboardingApi.get();
          setSnapshot(state);
          setDraft({
            person_type: 'PF',
            bank_account_type: 'CHECKING',
            ...state.draft
          });
          setStep(Math.max(2, state.step));
        } catch {
          setError(created ? 'Seu acesso está confirmado. Recarregue para continuar o cadastro.' : 'Não conseguimos confirmar a criação do acesso. Entre com seu e-mail e senha para retomar antes de tentar cadastrar novamente.');
          if (created) setLoadFailed(true);
        }
      } else setError(membersError(err));
    } finally {
      setBusy(false);
    }
  }
  async function next(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!member || !snapshot || locked) return;
    const fields = selectedFields(draft, step === 2 ? holderKeys : step === 3 ? addressKeys : bankKeys);
    if (step === 2) {
      fields.income_value = normalizeIncome(String(draft.income_value || '')) || undefined;
      if (!fields.income_value || Number(fields.income_value) <= 0 || Number(fields.income_value) > 1000000000) {
        setError('Informe uma renda maior que zero em reais, por exemplo 1.500,50.');
        return;
      }
      fields.cpf_cnpj = normalizeDocument(String(draft.cpf_cnpj || ''));
      if (draft.person_type === 'PF') {
        fields.company_type = null;
        if (!/^\d{11}$/.test(fields.cpf_cnpj)) {
          setError('Informe um CPF com 11 dígitos.');
          return;
        }
      } else {
        fields.birth_date = null;
        if (!/^[A-Z0-9]{12}\d{2}$/.test(fields.cpf_cnpj)) {
          setError('Confira o CNPJ informado.');
          return;
        }
      }
    }
    if (step === 3) { setPostalRequest(null); setPostalStatus(''); }
    if (step === 4) {
      fields.bank_holder_name = draft.legal_name;
      fields.bank_holder_cpf_cnpj = normalizeDocument(String(draft.cpf_cnpj || ''));
    }
    setBusy(true);
    setError('');
    try {
      const state = await membersOnboardingApi.save({
        revision: snapshot.revision,
        step: step + 1,
        draft: fields
      }, member.csrf_token);
      setSnapshot(state);
      setDraft(current => ({
        ...state.draft,
        ...current,
        ...fields
      }));
      setStep(step + 1);
    } catch (err) {
      setError(membersError(err));
    } finally {
      setBusy(false);
    }
  }
  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!member || !snapshot || !consent || uncertain) return;
    setBusy(true);
    setError('');
    try {
      const state = await membersOnboardingApi.submit(snapshot.revision, snapshot.consent_version, member.csrf_token);
      setSnapshot(state);
      setDraft(state.draft);
    } catch (err) {
      setError(membersError(err));
      // Confere o estado antes de liberar outra tentativa após resposta incerta.
      try {
        const state = await membersOnboardingApi.get();
        setSnapshot(state);
        setUncertain(false);
      } catch {
        setUncertain(true);
        setError('Não foi possível confirmar o envio. Atualize a situação antes de tentar novamente.');
      }
    } finally {
      setBusy(false);
    }
  }
  const reviewSection = (title: string, target: number, content: React.ReactNode) => <section className="rounded-xl border border-[var(--members-border)] p-4">
    <div className="mb-3 flex justify-between gap-3">
      <h3 className="text-sm font-medium">{title}</h3>{!locked && <button type="button" className="members-link flex items-center gap-1 text-xs" onClick={() => {
        setStep(target);
        setConsent(false);
      }}>
        <Pencil size={13} />Editar</button>}</div>
    <div className="members-secondary space-y-1 break-words text-sm">{content}</div>
  </section>;
  return <MembersShell action={member ? <Link className="members-link text-sm" to="/members/portal">Minha área</Link> : undefined}>

    <div className="mb-8">
      <h1 className="text-2xl font-semibold sm:text-[32px]">Comece sua parceria</h1>
      <p className="members-secondary mt-3 text-sm">Agências, profissionais de marketing e clientes Sminy podem indicar clínicas. Complete seu cadastro em cinco etapas; você pode voltar e revisar seus dados.</p>
    </div>

    <ol aria-label="Etapas do cadastro" className="mb-8 grid grid-cols-5">{labels.map((label, index) => <li key={label} aria-current={step === index + 1 ? 'step' : undefined} className="relative min-w-0 text-center">
        {index < labels.length - 1 && <span aria-hidden="true" className={`pointer-events-none absolute left-1/2 top-4 h-px w-full ${step > index + 1 ? 'bg-sminy-primary' : 'bg-[var(--members-border)]'}`} />}
        <span className={`relative z-[1] mx-auto mb-2 flex h-8 w-8 items-center justify-center rounded-full text-sm ${step >= index + 1 ? 'bg-sminy-primary text-white' : 'border border-[var(--members-border)] bg-[var(--members-canvas)] members-muted'}`}>{step > index + 1 ? <Check size={16} /> : index + 1}</span>
        <span className="block text-[10px] sm:text-xs">{label}</span>
      </li>)}</ol>

    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">

      <Card>
        {loading ? <Loading /> : loadFailed ? <Notice error>{error}
          <button className="underline" onClick={() => setRetry(value => value + 1)}>Tentar novamente</button>
        </Notice> : <>
          <h2 className="mb-5 text-lg font-medium">{labels[step - 1]}</h2>
          {step === 1 ? <form onSubmit={register} className="space-y-4">

            {registrationUnavailable && <Notice>O cadastro de parceiros estará disponível em breve. <button type="button" className="underline" onClick={() => setRetry(value => value + 1)}>Verificar disponibilidade</button></Notice>}
            <fieldset disabled={busy || !!member || registrationUnavailable} className="space-y-4">
              <Field label="Nome completo" name="name" defaultValue={member?.name} autoComplete="name" required minLength={2} maxLength={150} />
              <Field label="E-mail" name="email" defaultValue={member?.email} type="email" autoComplete="email" required maxLength={254} />
              <MembersMaskedField label="Telefone com DDD" name="phone" value={phone} onValueChange={value => setPhone(formatPhone(value))} type="tel" inputMode="tel" autoComplete="tel-national" placeholder="(11) 99999-9999" pattern="\([1-9][0-9]\) 9[0-9]{4}-[0-9]{4}" title="Informe um celular com DDD, como (11) 99999-9999" required />{!member && <Field label="Senha" name="password" type="password" autoComplete="new-password" minLength={12} maxLength={72} required />}{!member && <MembersMarketingOptIn />}</fieldset>

            <p className="members-muted text-xs">{member ? 'Seu acesso já foi criado. Continue para revisar o titular da conta.' : 'Use uma senha com pelo menos 12 caracteres. Após criar seu acesso, os próximos passos poderão ser retomados.'}</p>

            <button className={primary} disabled={busy || registrationUnavailable}>{busy ? 'Aguarde...' : member ? 'Continuar' : 'Criar acesso e continuar'}</button>
            <p className="text-sm">
              <Link className="members-link underline" to="/members/login">Já tenho uma conta</Link>
            </p>
          </form> : step < 5 ? <form onSubmit={next} className="space-y-5">
            <fieldset disabled={busy || locked} className="space-y-4">
            {step === 2 && <><Select label="Tipo de titular" name="person_type" value={draft.person_type || 'PF'} onChange={value => change('person_type', value)}>
                  <option value="PF">Pessoa física</option>
                  <option value="PJ">Pessoa jurídica</option>
                </Select>{field(draft.person_type === 'PJ' ? 'Razão social' : 'Nome completo do titular', 'legal_name', {
                  minLength: 2,
                  maxLength: 160
                })}{field(draft.person_type === 'PJ' ? 'CNPJ' : 'CPF', 'cpf_cnpj', {
                  maxLength: 18,
                  inputMode: draft.person_type === 'PJ' ? 'text' : 'numeric'
                })}{draft.person_type === 'PJ' ? <Select label="Tipo de empresa" name="company_type" value={draft.company_type || ''} onChange={value => change('company_type', value)}>
                  <option value="">Selecione</option>
                  <option value="MEI">MEI</option>
                  <option value="LIMITED">Sociedade limitada</option>
                  <option value="INDIVIDUAL">Empresa individual</option>
                  <option value="ASSOCIATION">Associação</option>
                </Select> : field('Data de nascimento', 'birth_date', {
                  type: 'date'
                })}<MembersMaskedField label="Renda ou faturamento mensal (R$)" name="income_value" value={formatIncome(draft.income_value || '')} onValueChange={value => change('income_value', incomeFromDigits(value))} inputMode="numeric" placeholder="R$ 0,00" maxLength={24} onPaste={event => {
                  event.preventDefault();
                  const value = normalizeIncome(event.clipboardData.getData('text').replace(/^\s*R\$\s*/, '').trim());
                  if (value) { change('income_value', value); setError(''); }
                  else setError('Cole um valor em reais com até duas casas decimais.');
                }} required /></>}
            {step === 3 && <><MembersMaskedField label="CEP" name="postal_code" value={formatPostalCode(draft.postal_code || '')} onValueChange={value => {
                  const cep = digits(value).slice(0, 8);
                  change('postal_code', cep);
                  setPostalStatus('');
                  setPostalRequest(cep.length === 8 ? {cep, baseline: {...draft}} : null);
                }} autoComplete="postal-code" inputMode="numeric" placeholder="00000-000" pattern="[0-9]{5}-[0-9]{3}" required aria-describedby="members-postal-status" />
                <p id="members-postal-status" role="status" className="members-secondary text-sm">{postalStatus || 'Digite o CEP para preencher o endereço automaticamente.'}</p>{field('Endereço', 'address', {
                  autoComplete: 'street-address',
                  maxLength: 160
                })}<div className="grid gap-4 sm:grid-cols-2">{field('Número', 'address_number', {
                    maxLength: 20
                  })}{field('Complemento', 'complement', {
                    required: false,
                    maxLength: 100
                  })}</div>{field('Bairro', 'province', {
                  minLength: 2,
                  maxLength: 100
                })}<div className="grid gap-4 sm:grid-cols-2">{field('Cidade', 'city', {
                    minLength: 2,
                    maxLength: 100
                  })}<Select label="Estado (UF)" name="state" value={(draft.state || '').toUpperCase()} onChange={value => change('state', value)}><option value="">Selecione a UF</option>{states.map(([code, name]) => <option key={code} value={code}>{code} — {name}</option>)}</Select></div></>}
            {step === 4 && <><Notice>A conta de destino deve pertencer a {draft.legal_name}. Confira agência, conta e dígitos antes de continuar.</Notice><div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Titular da conta" value={draft.legal_name || ''} readOnly />
                  <Field label="CPF/CNPJ do titular" value={draft.cpf_cnpj || ''} readOnly />
                </div><MembersBankField value={draft.bank_code || ''} onChange={value => change('bank_code', value)} /><div className="grid gap-4 sm:grid-cols-2">{field('Agência', 'bank_agency', {
                    inputMode: 'numeric',
                    maxLength: 6,
                    pattern: '[0-9]{1,6}'
                  })}{field('Dígito da agência', 'bank_agency_digit', {
                    required: false,
                    maxLength: 2,
                    pattern: '[0-9Xx]{1,2}'
                  })}</div><div className="grid gap-4 sm:grid-cols-2">{field('Conta', 'bank_account', {
                    inputMode: 'numeric',
                    maxLength: 20,
                    pattern: '[0-9]{1,20}'
                  })}{field('Dígito da conta', 'bank_account_digit', {
                    maxLength: 2,
                    pattern: '[0-9Xx]{1,2}'
                  })}</div><Select label="Tipo da conta" name="bank_account_type" value={draft.bank_account_type || 'CHECKING'} onChange={value => change('bank_account_type', value)}>
                  <option value="CHECKING">Conta corrente</option>
                  <option value="SAVINGS">Conta poupança</option>
                </Select></>}
          </fieldset>
            <div className="flex flex-wrap justify-between gap-3">
              <button type="button" className="members-link flex items-center gap-2 text-sm" disabled={busy} onClick={() => {
                setStep(value => value - 1);
                setError('');
              }}>
                <ArrowLeft size={16} />Voltar</button>
              <button className={primary} disabled={busy || locked}>{busy ? 'Salvando...' : 'Salvar e continuar'}</button>
            </div>
          </form> : <form onSubmit={submit} className="space-y-4">
            {reviewSection('Titular', 2, <><p>{draft.legal_name}</p><p>{draft.cpf_cnpj}</p><p>Renda mensal: {draft.income_value ? formatIncome(draft.income_value) : '—'}</p></>)}
            {reviewSection('Endereço', 3, <><p>{draft.address}, {draft.address_number}{draft.complement ? ` · ${draft.complement}` : ''}</p><p>{draft.province} · {draft.city}/{draft.state} · {formatPostalCode(draft.postal_code || '')}</p></>)}
            {reviewSection('Conta bancária', 4, <><p>{draft.bank_holder_name || draft.legal_name}</p><p>Banco {draft.bank_code} · Agência {draft.bank_agency}{draft.bank_agency_digit ? `-${draft.bank_agency_digit}` : ''}</p><p>Conta {draft.bank_account}-{draft.bank_account_digit} · {draft.bank_account_type === 'SAVINGS' ? 'Poupança' : 'Corrente'}</p></>)}
            {snapshot?.status === 'awaiting_activation' ? <Notice>Cadastro recebido. A Sminy está preparando a ativação da conta de recebimento. Seus dados permanecem salvos.</Notice> : snapshot?.status === 'submitted' || snapshot?.provisioning_status === 'created' ? <Notice>Cadastro enviado ao Asaas. Acompanhe os documentos e a análise cadastral na sua área.</Notice> : locked ? <Notice>Estamos conferindo a criação da sua conta. Seus dados ficam protegidos contra alterações durante essa etapa.</Notice> : <label className="flex items-start gap-3 text-sm leading-relaxed">
              <input type="checkbox" required checked={consent} onChange={event => setConsent(event.target.checked)} className="mt-1" />
              <span>Confirmo os dados e autorizo seu uso para abertura da conta de recebimento no Asaas. Declaro que a conta bancária pertence ao titular informado.</span>
            </label>}
            {!locked && <button className={primary} disabled={busy || !consent}>{busy ? 'Enviando...' : 'Concluir cadastro'}</button>}
            {locked && <div className="flex flex-wrap gap-3">
              <Link className={primary} to="/members/portal">Acompanhar na minha área</Link>
              <button type="button" className="members-input text-sm" disabled={busy} onClick={() => setRetry(value => value + 1)}>Atualizar situação</button>
            </div>}
          </form>}
          {error && <div className="mt-5">
            <Notice error>{error}{member && <button className="ml-2 underline" onClick={() => setRetry(value => value + 1)}>{uncertain ? 'Atualizar situação' : 'Recarregar dados salvos'}</button>}</Notice>
          </div>}
        </>}
      </Card>

      <AsaasExplanation />
    </div>
  </MembersShell>;
}
